import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import { PermissionEnum } from '@Application/Security/Roles';
import MarkWorkflowRunHandled from '@Application/WorkflowRuns/Handling/Commands/MarkWorkflowRunHandled';
import UnmarkWorkflowRunHandled from '@Application/WorkflowRuns/Handling/Commands/UnmarkWorkflowRunHandled';
import markWorkflowRunHandled from '@Application/WorkflowRuns/Handling/markWorkflowRunHandled';
import unmarkWorkflowRunHandled from '@Application/WorkflowRuns/Handling/unmarkWorkflowRunHandled';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import WithPermission from '@UI/Providers/PermissionProvider/WithPermission';
import type FacilityWorkflowRunView from '@UI/Store/WorkflowRuns/Views/FacilityWorkflowRunView';
import { CheckCheck, Eye } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const HANDLING_PERMISSIONS = [PermissionEnum.INTERVIEW_WRITE, PermissionEnum.DOCUMENT_GENERATION_LAUNCH];

/** Statuses a reader can mark as handled: the run ended without fully succeeding. */
const MARKABLE_STATUSES: readonly string[] = ['failed', 'partial'];

type Props = {
	readonly run: FacilityWorkflowRunView;
	readonly subject: SubjectAddress;
};

/**
 * Marks the run as handled, or puts it back under watch. Reversible, hence no confirmation.
 * `run.canChangeHandling` is the server's verdict and comes on top of the permission gate.
 */
const WorkflowRunCardActions: React.FC<Props> = ({ run, subject }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const [isPending, setIsPending] = React.useState(false);

	const handleMark = React.useCallback(() => {
		setIsPending(true);

		dispatch(markWorkflowRunHandled(new MarkWorkflowRunHandled(currentFacility.id, subject, run.id)))
			.catch(ignoreHandledError) // the toast comes from WorkflowRunsNotifications
			.finally(() => setIsPending(false));
	}, [dispatch, currentFacility.id, subject, run.id]);

	const handleUnmark = React.useCallback(() => {
		setIsPending(true);

		dispatch(unmarkWorkflowRunHandled(new UnmarkWorkflowRunHandled(currentFacility.id, subject, run.id)))
			.catch(ignoreHandledError) // the toast comes from WorkflowRunsNotifications
			.finally(() => setIsPending(false));
	}, [dispatch, currentFacility.id, subject, run.id]);

	if (!run.canChangeHandling) {
		return null;
	}

	if (run.status.value === 'handled') {
		return (
			<WithPermission permission={HANDLING_PERMISSIONS} mode="any">
				<Button
					type="button"
					severity="secondary"
					label="Remettre à surveiller"
					icon={<Eye size={16} />}
					loading={isPending}
					onClick={handleUnmark}
				/>
			</WithPermission>
		);
	}

	if (!MARKABLE_STATUSES.includes(run.status.value)) {
		return null;
	}

	return (
		<WithPermission permission={HANDLING_PERMISSIONS} mode="any">
			<Button
				type="button"
				label="Marquer comme traité"
				icon={<CheckCheck size={16} />}
				loading={isPending}
				onClick={handleMark}
			/>
		</WithPermission>
	);
};

export default WorkflowRunCardActions;
